import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type ViewportCenterPercent = {x: number; y: number};

export type DragSummary = {
  movementX: number;
  movementY: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
};

type Options = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Fraction (0–100) of content where the viewport center should stay. The
   * caller owns this state. Every scroll and final drag position reaches the
   * setter, including clamped restores. A consumer sharing the center across
   * containers should publish here only when isDragScroll is true, and use
   * onUserScroll for wheel scrolling. isDragScroll requires actual scrolling.
   */
  viewportCenterPercent: ViewportCenterPercent;
  setViewportCenterPercent: (
    center: ViewportCenterPercent,
    isDragScroll: boolean
  ) => void;
  /**
   * Values whose change re-applies the stored center to the scroll position
   * (zoom level, content size). Feeds a dependency list, so the array must
   * keep the same length on every render.
   */
  restoreKeys: readonly unknown[];
  /** User-initiated scrolls only; restores and drag panning are filtered out. */
  onUserScroll?: (el: HTMLDivElement, next: ViewportCenterPercent) => void;
  onDragMove?: (drag: Pick<DragSummary, 'movementX' | 'movementY'>) => void;
  onDragEnd?: (drag: DragSummary) => void;
};

function getViewportCenter(el: HTMLDivElement): ViewportCenterPercent {
  return {
    x: ((el.scrollLeft + el.clientWidth / 2) / el.scrollWidth) * 100,
    y: ((el.scrollTop + el.clientHeight / 2) / el.scrollHeight) * 100,
  };
}

/**
 * Drag-to-pan over a scrollable media container, keeping the viewport center
 * as a percentage of the content so it survives zoom changes. Dragging pans
 * via scrollBy and ends on mouse release anywhere, including outside the
 * container. Scrolls the hook itself causes are not reported as user scrolls.
 */
export function useScrollCenterPan({
  containerRef,
  viewportCenterPercent,
  setViewportCenterPercent,
  restoreKeys,
  onUserScroll,
  onDragMove,
  onDragEnd,
}: Options) {
  const [isPanning, setIsPanning] = useState(false);
  const activeDrag = useRef<{
    mouseX: number;
    mouseY: number;
    scrollLeft: number;
    scrollTop: number;
    hasScrolled: boolean;
  } | null>(null);
  const programmaticScrollPosition = useRef<{
    left: number;
    top: number;
  } | null>(null);

  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      // Only the primary button pans; context menus and middle-click
      // autoscroll keep their default behavior.
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      activeDrag.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        scrollLeft: event.currentTarget.scrollLeft,
        scrollTop: event.currentTarget.scrollTop,
        hasScrolled: false,
      };
      setIsPanning(true);
    },
    []
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const el = containerRef.current;
      const drag = activeDrag.current;
      if (drag == null || el == null) {
        return;
      }
      event.preventDefault();
      onDragMove?.({
        movementX: event.clientX - drag.mouseX,
        movementY: event.clientY - drag.mouseY,
      });
      const left = el.scrollLeft;
      const top = el.scrollTop;
      el.scrollBy(-event.movementX, -event.movementY);
      if (el.scrollLeft !== left || el.scrollTop !== top) {
        drag.hasScrolled = true;
      }
      programmaticScrollPosition.current = {
        left: el.scrollLeft,
        top: el.scrollTop,
      };
    };
    const handleMouseUp = (event: MouseEvent) => {
      const start = activeDrag.current;
      if (event.button !== 0 || start == null) {
        return;
      }
      activeDrag.current = null;
      const el = containerRef.current;
      if (
        el != null &&
        (start.hasScrolled ||
          el.scrollLeft !== start.scrollLeft ||
          el.scrollTop !== start.scrollTop)
      ) {
        // The final scroll event may arrive after release. Save its center
        // before ending the drag lets the layout effect restore it.
        setViewportCenterPercent(getViewportCenter(el), true);
      }
      setIsPanning(false);
      onDragEnd?.({
        movementX: event.clientX - start.mouseX,
        movementY: event.clientY - start.mouseY,
        startX: start.mouseX,
        startY: start.mouseY,
        endX: event.clientX,
        endY: event.clientY,
      });
    };
    // Window-level listeners keep the drag alive outside the container and
    // end it on release anywhere. Capture mouseup before the container or
    // its descendants stop propagation, so onDragEnd runs before the click.
    // The ref gates input immediately, without waiting for a React commit.
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp, true);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [containerRef, onDragMove, onDragEnd, setViewportCenterPercent]);

  // The modal suppressed every mouseup over the container; the window
  // listener above ends the drag, this keeps ancestors from also acting.
  const onMouseUp: React.MouseEventHandler<HTMLDivElement> = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget;
      const next = getViewportCenter(el);
      const drag = activeDrag.current;
      if (
        drag != null &&
        (el.scrollLeft !== drag.scrollLeft || el.scrollTop !== drag.scrollTop)
      ) {
        drag.hasScrolled = true;
      }
      setViewportCenterPercent(next, drag?.hasScrolled ?? false);
      const position = programmaticScrollPosition.current;
      if (position?.left === el.scrollLeft && position.top === el.scrollTop) {
        return;
      }
      programmaticScrollPosition.current = null;
      onUserScroll?.(el, next);
    },
    [setViewportCenterPercent, onUserScroll]
  );

  // After zoom or a content-size change, set scroll so the viewport center
  // stays at the same point in the content. Never during a drag: the drag's
  // scrollBy deltas land between center writes and React commits, so a
  // restore here would revert them and eat movement under fast input. The
  // isPanning dependency re-applies the center once the drag ends.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || isPanning) {
      return;
    }
    const canScroll =
      el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    if (!canScroll) {
      return;
    }
    const centerX = (el.scrollWidth * viewportCenterPercent.x) / 100;
    const centerY = (el.scrollHeight * viewportCenterPercent.y) / 100;
    el.scrollLeft = Math.max(
      0,
      Math.min(centerX - el.clientWidth / 2, el.scrollWidth - el.clientWidth)
    );
    el.scrollTop = Math.max(
      0,
      Math.min(centerY - el.clientHeight / 2, el.scrollHeight - el.clientHeight)
    );
    // Scroll events can arrive after the next frame, or not fire at all.
    // Match the actual restored position instead of clearing a timed flag.
    programmaticScrollPosition.current = {
      left: el.scrollLeft,
      top: el.scrollTop,
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [...restoreKeys, viewportCenterPercent, containerRef, isPanning]);

  return {isPanning, onMouseDown, onMouseUp, onScroll};
}
